/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Module Management Page
 * ============================================================================
 *
 * Elegant admin interface for managing platform modules and dependencies.
 * Features:
 * - Visual module cards with status indicators
 * - Interactive dependency graph
 * - Dependency creation with validation
 * - Real-time dependency enforcement
 *
 * ============================================================================
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Link2,
  Link2Off,
  Plus,
  Trash2,
  ChevronRight,
  Check,
  X,
  AlertTriangle,
  Info,
  Loader2,
  GitBranch,
  Lock,
  Unlock,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useModules, useCreateDependency, useDeleteDependency } from '@/hooks/api/useModules';
import { useNotification } from '@/hooks';
import { cn } from '@/lib/utils';
import { ModuleManagement, ModuleDependency } from '@/services/superadmin/modules.service';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';
import { ConfirmationDialog } from '@/components/ui';

// Module icons mapping
const MODULE_ICONS: Record<string, string> = {
  CORE: '🏢',
  VENTA: '💼',
  HR: '👥',
  COMPTA: '📊',
  PM: '📋',
  CRM: '🤝',
};

// Module colors
const MODULE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  CORE: { bg: 'rgba(107, 114, 128, 0.15)', border: '#6B7280', text: '#6B7280' },
  VENTA: { bg: 'rgba(59, 130, 246, 0.15)', border: '#3B82F6', text: '#3B82F6' },
  HR: { bg: 'rgba(16, 185, 129, 0.15)', border: '#10B981', text: '#10B981' },
  COMPTA: { bg: 'rgba(139, 92, 246, 0.15)', border: '#8B5CF6', text: '#8B5CF6' },
  PM: { bg: 'rgba(245, 158, 11, 0.15)', border: '#F59E0B', text: '#F59E0B' },
  CRM: { bg: 'rgba(236, 72, 153, 0.15)', border: '#EC4899', text: '#EC4899' },
};

const getModuleColor = (code: string) =>
  MODULE_COLORS[code] || { bg: 'rgba(139, 0, 0, 0.15)', border: '#8B0000', text: '#8B0000' };

// Add Dependency Modal
interface AddDependencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  modules: ModuleManagement[];
  sourceModule?: ModuleManagement;
  onSubmit: (moduleCode: string, dependsOnCode: string, isHard: boolean, description: string) => void;
  isLoading: boolean;
}

const AddDependencyModal: React.FC<AddDependencyModalProps> = ({
  isOpen,
  onClose,
  modules,
  sourceModule,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation('platform');
  const [selectedModule, setSelectedModule] = useState(sourceModule?.code || '');
  const [dependsOn, setDependsOn] = useState('');
  const [isHardDependency, setIsHardDependency] = useState(true);
  const [description, setDescription] = useState('');

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedModule(sourceModule?.code || '');
      setDependsOn('');
      setIsHardDependency(true);
      setDescription('');
    }
  }, [isOpen, sourceModule]);

  // Get available target modules (exclude self and existing dependencies)
  const availableTargets = useMemo(() => {
    if (!selectedModule) return modules.filter(m => !m.isRequired);
    const currentModule = modules.find(m => m.code === selectedModule);
    const existingDeps = currentModule?.dependencies.map(d => d.dependsOnCode) || [];
    return modules.filter(m =>
      m.code !== selectedModule &&
      !existingDeps.includes(m.code)
    );
  }, [selectedModule, modules]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedModule && dependsOn) {
      onSubmit(selectedModule, dependsOn, isHardDependency, description);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-container">
        <div className="modal-header">
          <div className="modal-icon">
            <Link2 size={20} />
          </div>
          <div>
            <h2 className="modal-title">{t('modules.addDependency.title', 'Add Dependency')}</h2>
            <p className="modal-subtitle">{t('modules.addDependency.subtitle', 'Create a dependency relationship between modules')}</p>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="dependency-visual">
            <div className={cn('dep-box', selectedModule && 'selected')}>
              {selectedModule ? (
                <>
                  <span className="dep-icon">{MODULE_ICONS[selectedModule] || '📦'}</span>
                  <span>{modules.find(m => m.code === selectedModule)?.name || selectedModule}</span>
                </>
              ) : (
                <span className="dep-placeholder">{t('modules.addDependency.selectModule', 'Select module')}</span>
              )}
            </div>
            <div className="dep-arrow">
              <ChevronRight size={20} />
              <span className="dep-arrow-label">{isHardDependency ? t('modules.requires', 'requires') : t('modules.suggests', 'suggests')}</span>
            </div>
            <div className={cn('dep-box', dependsOn && 'selected')}>
              {dependsOn ? (
                <>
                  <span className="dep-icon">{MODULE_ICONS[dependsOn] || '📦'}</span>
                  <span>{modules.find(m => m.code === dependsOn)?.name || dependsOn}</span>
                </>
              ) : (
                <span className="dep-placeholder">{t('modules.addDependency.selectTarget', 'Select target')}</span>
              )}
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>{t('modules.addDependency.module', 'Module')}</label>
              <select
                value={selectedModule}
                onChange={(e) => {
                  setSelectedModule(e.target.value);
                  setDependsOn('');
                }}
                disabled={!!sourceModule || isLoading}
              >
                <option value="">{t('common.select', 'Select...')}</option>
                {modules.filter(m => !m.isRequired).map(m => (
                  <option key={m.code} value={m.code}>{m.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('modules.addDependency.dependsOn', 'Depends On')}</label>
              <select
                value={dependsOn}
                onChange={(e) => setDependsOn(e.target.value)}
                disabled={!selectedModule || isLoading}
              >
                <option value="">{t('common.select', 'Select...')}</option>
                {availableTargets.map(m => (
                  <option key={m.code} value={m.code}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>{t('modules.addDependency.type', 'Dependency Type')}</label>
            <div className="dep-type-options">
              <button
                type="button"
                className={cn('dep-type-btn', isHardDependency && 'active')}
                onClick={() => setIsHardDependency(true)}
              >
                <Lock size={16} />
                <div>
                  <span className="dep-type-title">{t('modules.addDependency.required', 'Required')}</span>
                  <span className="dep-type-desc">{t('modules.addDependency.requiredDesc', 'Module cannot work without this dependency')}</span>
                </div>
              </button>
              <button
                type="button"
                className={cn('dep-type-btn', !isHardDependency && 'active')}
                onClick={() => setIsHardDependency(false)}
              >
                <Unlock size={16} />
                <div>
                  <span className="dep-type-title">{t('modules.addDependency.suggested', 'Suggested')}</span>
                  <span className="dep-type-desc">{t('modules.addDependency.suggestedDesc', 'Module works better with this dependency')}</span>
                </div>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>{t('modules.addDependency.description', 'Description')} <span className="optional">({t('common.optional', 'optional')})</span></label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('modules.addDependency.descPlaceholder', 'Why is this dependency needed?')}
              disabled={isLoading}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isLoading}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!selectedModule || !dependsOn || isLoading}
            >
              {isLoading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
              {t('modules.addDependency.create', 'Create Dependency')}
            </button>
          </div>
        </form>
      </div>
      <style>{modalStyles}</style>
    </>
  );
};

// Module Card Component
interface ModuleCardProps {
  module: ModuleManagement;
  allModules: ModuleManagement[];
  onAddDependency: () => void;
  onRemoveDependency: (dep: ModuleDependency) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  module,
  allModules,
  onAddDependency,
  onRemoveDependency,
  isExpanded,
  onToggleExpand,
}) => {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const color = getModuleColor(module.code);

  const getModuleName = (code: string) =>
    allModules.find(m => m.code === code)?.name || code;

  return (
    <div
      className={cn('module-card', isExpanded && 'expanded')}
      style={{ '--module-color': color.border, '--module-bg': color.bg } as React.CSSProperties}
    >
      <div className="module-header" onClick={onToggleExpand}>
        <div className="module-icon-wrapper">
          <span className="module-icon">{MODULE_ICONS[module.code] || '📦'}</span>
          {module.isRequired && (
            <span className="module-badge required" title={t('modules.coreModule', 'Core Module')}>
              <Lock size={10} />
            </span>
          )}
        </div>
        <div className="module-info">
          <div className="module-name-row">
            <h3 className="module-name">{module.name}</h3>
            <span className="module-version">v{module.version}</span>
          </div>
          <p className="module-description">{module.description || t('modules.noDescription', 'No description')}</p>
        </div>
        <div className="module-status">
          {module.isActive ? (
            <span className="status-badge active">
              <Check size={12} /> {t('modules.active', 'Active')}
            </span>
          ) : (
            <span className="status-badge inactive">
              <X size={12} /> {t('modules.inactive', 'Inactive')}
            </span>
          )}
        </div>
        <ChevronRight className={cn('expand-icon', isExpanded && 'rotated')} size={20} />
      </div>

      {isExpanded && (
        <div className="module-details">
          {/* Stats Row */}
          <div className="module-stats">
            <div className="stat-item">
              <span className="stat-value">{module.tierCount}</span>
              <span className="stat-label">{t('modules.tiers', 'Pricing Tiers')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{module.features.length}</span>
              <span className="stat-label">{t('modules.features', 'Features')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{module.dependencies.length}</span>
              <span className="stat-label">{t('modules.dependencies', 'Dependencies')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{module.dependentModules.length}</span>
              <span className="stat-label">{t('modules.dependents', 'Dependents')}</span>
            </div>
          </div>

          {/* Dependencies Section */}
          <div className="module-section">
            <div className="section-header">
              <h4>
                <GitBranch size={14} />
                {t('modules.dependenciesTitle', 'Dependencies')}
              </h4>
              {!module.isRequired && (
                <button className="btn-add-dep" onClick={onAddDependency}>
                  <Plus size={14} />
                  {t('modules.addDep', 'Add')}
                </button>
              )}
            </div>
            {module.dependencies.length > 0 ? (
              <div className="dependency-list">
                {module.dependencies.map((dep) => (
                  <div key={dep.dependsOnCode} className="dependency-item">
                    <span className="dep-module-icon">{MODULE_ICONS[dep.dependsOnCode] || '📦'}</span>
                    <div className="dep-info">
                      <span className="dep-name">{getModuleName(dep.dependsOnCode)}</span>
                      <span className={cn('dep-type', dep.isRequired ? 'required' : 'suggested')}>
                        {dep.isRequired ? (
                          <><Lock size={10} /> {t('modules.required', 'Required')}</>
                        ) : (
                          <><Unlock size={10} /> {t('modules.suggested', 'Suggested')}</>
                        )}
                      </span>
                    </div>
                    {dep.description && (
                      <span className="dep-description" title={dep.description}>
                        <Info size={12} />
                      </span>
                    )}
                    {!module.isRequired && (
                      <button
                        className="btn-remove-dep"
                        onClick={() => onRemoveDependency(dep)}
                        title={t('modules.removeDep', 'Remove dependency')}
                      >
                        <Link2Off size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-items">{t('modules.noDependencies', 'No dependencies')}</p>
            )}
          </div>

          {/* Dependents Section */}
          {module.dependentModules.length > 0 && (
            <div className="module-section">
              <div className="section-header">
                <h4>
                  <AlertTriangle size={14} />
                  {t('modules.dependentsTitle', 'Modules that depend on this')}
                </h4>
              </div>
              <div className="dependent-list">
                {module.dependentModules.map((code) => (
                  <span key={code} className="dependent-badge">
                    {MODULE_ICONS[code] || '📦'} {getModuleName(code)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Features Section */}
          {module.features.length > 0 && (
            <div className="module-section">
              <div className="section-header">
                <h4>
                  <Sparkles size={14} />
                  {t('modules.featuresTitle', 'Features')}
                </h4>
              </div>
              <div className="feature-list">
                {module.features.map((feature) => (
                  <span key={feature} className="feature-badge">{feature}</span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="module-actions">
            <button
              className="btn-secondary"
              onClick={() => navigate('/superadmin/module-pricing')}
            >
              <ExternalLink size={14} />
              {t('modules.managePricing', 'Manage Pricing')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Page Component
export default function ModuleManagementPage() {
  const { t } = useTranslation('platform');
  const { success, error: showError } = useNotification();

  // State
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [addDepModalOpen, setAddDepModalOpen] = useState(false);
  const [selectedModuleForDep, setSelectedModuleForDep] = useState<ModuleManagement | undefined>();
  const [depToDelete, setDepToDelete] = useState<{ moduleCode: string; dep: ModuleDependency } | null>(null);

  // Data fetching
  const { data: modules = [], isLoading, refetch } = useModules();
  const createDependency = useCreateDependency();
  const deleteDependency = useDeleteDependency();

  // Handlers
  const toggleExpand = useCallback((code: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, []);

  const handleAddDependency = useCallback((module?: ModuleManagement) => {
    setSelectedModuleForDep(module);
    setAddDepModalOpen(true);
  }, []);

  const handleCreateDependency = useCallback(async (
    moduleCode: string,
    dependsOnCode: string,
    isHard: boolean,
    description: string
  ) => {
    try {
      await createDependency.mutateAsync({
        moduleCode,
        dependsOnCode,
        isHardDependency: isHard,
        description: description || undefined,
      });
      success(t('modules.dependencyCreated', 'Dependency created successfully'));
      setAddDepModalOpen(false);
      refetch();
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('common.error', 'An error occurred') };
      showError(message);
    }
  }, [createDependency, success, showError, t, refetch]);

  const handleDeleteDependency = useCallback(async () => {
    if (!depToDelete) return;

    try {
      await deleteDependency.mutateAsync({
        moduleCode: depToDelete.moduleCode,
        dependsOnCode: depToDelete.dep.dependsOnCode,
      });
      success(t('modules.dependencyDeleted', 'Dependency removed successfully'));
      setDepToDelete(null);
      refetch();
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('common.error', 'An error occurred') };
      showError(message);
    }
  }, [depToDelete, deleteDependency, success, showError, t, refetch]);

  // Separate core and optional modules
  const { coreModules, optionalModules } = useMemo(() => {
    const core = modules.filter(m => m.isRequired);
    const optional = modules.filter(m => !m.isRequired);
    return { coreModules: core, optionalModules: optional };
  }, [modules]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="module-management-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <Package className="w-6 h-6" />
            {t('modules.title', 'Module Management')}
          </h1>
          <p className="page-subtitle">
            {t('modules.subtitle', 'Manage platform modules and their dependencies')}
          </p>
        </div>
        <button className="btn-primary" onClick={() => handleAddDependency()}>
          <Link2 size={16} />
          {t('modules.addDependency.title', 'Add Dependency')}
        </button>
      </div>

      {/* Dependency Info Banner */}
      <div className="info-banner">
        <Info size={18} />
        <div>
          <strong>{t('modules.dependencyInfo.title', 'How Dependencies Work')}</strong>
          <p>
            {t('modules.dependencyInfo.description', 'When a module has a required dependency, organizations must select the parent module before they can subscribe to the dependent module. Suggested dependencies are optional but recommended for full functionality.')}
          </p>
        </div>
      </div>

      {/* Core Modules Section */}
      {coreModules.length > 0 && (
        <section className="module-section-group">
          <h2 className="section-title">
            <Lock size={16} />
            {t('modules.coreModules', 'Core Modules')}
            <span className="section-count">{coreModules.length}</span>
          </h2>
          <div className="modules-grid">
            {coreModules.map(module => (
              <ModuleCard
                key={module.code}
                module={module}
                allModules={modules}
                onAddDependency={() => handleAddDependency(module)}
                onRemoveDependency={(dep) => setDepToDelete({ moduleCode: module.code, dep })}
                isExpanded={expandedModules.has(module.code)}
                onToggleExpand={() => toggleExpand(module.code)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Optional Modules Section */}
      <section className="module-section-group">
        <h2 className="section-title">
          <Package size={16} />
          {t('modules.optionalModules', 'Optional Modules')}
          <span className="section-count">{optionalModules.length}</span>
        </h2>
        <div className="modules-grid">
          {optionalModules.map(module => (
            <ModuleCard
              key={module.code}
              module={module}
              allModules={modules}
              onAddDependency={() => handleAddDependency(module)}
              onRemoveDependency={(dep) => setDepToDelete({ moduleCode: module.code, dep })}
              isExpanded={expandedModules.has(module.code)}
              onToggleExpand={() => toggleExpand(module.code)}
            />
          ))}
        </div>
      </section>

      {/* Add Dependency Modal */}
      <AddDependencyModal
        isOpen={addDepModalOpen}
        onClose={() => setAddDepModalOpen(false)}
        modules={modules}
        sourceModule={selectedModuleForDep}
        onSubmit={handleCreateDependency}
        isLoading={createDependency.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={!!depToDelete}
        onCancel={() => setDepToDelete(null)}
        onConfirm={handleDeleteDependency}
        title={t('modules.deleteDependency.title', 'Remove Dependency')}
        message={t('modules.deleteDependency.message', `Are you sure you want to remove this dependency? Organizations with this module will no longer be required to have the parent module.`)}
        confirmText={t('common.remove', 'Remove')}
        variant="danger"
        isLoading={deleteDependency.isPending}
      />

      <style>{pageStyles}</style>
    </div>
  );
}

const pageStyles = `
  .module-management-page {
    max-width: 1200px;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
    gap: 16px;
    flex-wrap: wrap;
  }

  .page-title {
    font-family: var(--font-heading);
    font-size: 24px;
    font-weight: 700;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
  }

  .page-subtitle {
    font-size: 14px;
    color: var(--color-text-secondary);
    margin: 4px 0 0;
  }

  .btn-primary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: linear-gradient(135deg, #8B0000, #A31621);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(139, 0, 0, 0.3);
  }

  .btn-secondary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: transparent;
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  /* Info Banner */
  .info-banner {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 18px;
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.2);
    border-radius: 10px;
    margin-bottom: 24px;
    color: #3B82F6;
  }

  .info-banner strong {
    display: block;
    font-size: 13px;
    margin-bottom: 2px;
  }

  .info-banner p {
    font-size: 12px;
    opacity: 0.9;
    margin: 0;
    line-height: 1.5;
  }

  /* Section Groups */
  .module-section-group {
    margin-bottom: 32px;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .section-count {
    background: var(--color-bg-tertiary);
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 11px;
    color: var(--color-text-secondary);
  }

  /* Module Cards */
  .modules-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .module-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s;
  }

  .module-card:hover {
    border-color: var(--module-color);
  }

  .module-card.expanded {
    border-color: var(--module-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .module-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    cursor: pointer;
  }

  .module-icon-wrapper {
    position: relative;
  }

  .module-icon {
    font-size: 28px;
    line-height: 1;
  }

  .module-badge {
    position: absolute;
    bottom: -4px;
    right: -4px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-secondary);
    border: 2px solid var(--color-bg-secondary);
  }

  .module-badge.required {
    background: #6B7280;
    color: white;
  }

  .module-info {
    flex: 1;
    min-width: 0;
  }

  .module-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .module-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }

  .module-version {
    font-size: 11px;
    color: var(--color-text-muted);
    background: var(--color-bg-tertiary);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: var(--font-mono);
  }

  .module-description {
    font-size: 12px;
    color: var(--color-text-secondary);
    margin: 2px 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .module-status {
    flex-shrink: 0;
  }

  .status-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 600;
  }

  .status-badge.active {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
  }

  .status-badge.inactive {
    background: rgba(107, 114, 128, 0.15);
    color: #6B7280;
  }

  .expand-icon {
    color: var(--color-text-muted);
    transition: transform 0.2s;
    flex-shrink: 0;
  }

  .expand-icon.rotated {
    transform: rotate(90deg);
  }

  /* Module Details */
  .module-details {
    padding: 0 18px 18px;
    border-top: 1px solid var(--color-border-subtle);
    animation: slideDown 0.2s ease;
  }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .module-stats {
    display: flex;
    gap: 24px;
    padding: 16px 0;
    border-bottom: 1px solid var(--color-border-subtle);
    margin-bottom: 16px;
  }

  .stat-item {
    display: flex;
    flex-direction: column;
  }

  .stat-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--color-text-primary);
    font-family: var(--font-mono);
  }

  .stat-label {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .module-section {
    margin-bottom: 16px;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .section-header h4 {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary);
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .btn-add-dep {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: transparent;
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    font-size: 11px;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.15s;
  }

  .btn-add-dep:hover {
    background: var(--module-color);
    border-color: var(--module-color);
    color: white;
  }

  .no-items {
    font-size: 12px;
    color: var(--color-text-muted);
    font-style: italic;
    margin: 0;
  }

  /* Dependency List */
  .dependency-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .dependency-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
  }

  .dep-module-icon {
    font-size: 18px;
  }

  .dep-info {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .dep-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .dep-type {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 500;
  }

  .dep-type.required {
    background: rgba(239, 68, 68, 0.15);
    color: #EF4444;
  }

  .dep-type.suggested {
    background: rgba(59, 130, 246, 0.15);
    color: #3B82F6;
  }

  .dep-description {
    color: var(--color-text-muted);
    cursor: help;
  }

  .btn-remove-dep {
    padding: 6px;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.15s;
  }

  .btn-remove-dep:hover {
    background: rgba(239, 68, 68, 0.15);
    color: #EF4444;
  }

  /* Dependent & Feature Lists */
  .dependent-list,
  .feature-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .dependent-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: rgba(245, 158, 11, 0.15);
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: 6px;
    font-size: 12px;
    color: #F59E0B;
  }

  .feature-badge {
    padding: 4px 10px;
    background: var(--color-bg-tertiary);
    border-radius: 6px;
    font-size: 11px;
    color: var(--color-text-secondary);
    font-family: var(--font-mono);
  }

  .module-actions {
    display: flex;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid var(--color-border-subtle);
    margin-top: 8px;
  }

  @media (max-width: 768px) {
    .module-stats {
      flex-wrap: wrap;
      gap: 16px;
    }

    .dep-info {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }
  }
`;

const modalStyles = `
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    z-index: 9998;
    animation: fadeIn 0.15s ease;
  }

  .modal-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 520px;
    max-height: 90vh;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    z-index: 9999;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    animation: slideIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translate(-50%, -48%) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  .modal-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }

  .modal-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(139, 0, 0, 0.2), rgba(139, 0, 0, 0.1));
    border: 1px solid rgba(139, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8B0000;
    flex-shrink: 0;
  }

  .modal-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }

  .modal-subtitle {
    font-size: 12px;
    color: var(--color-text-muted);
    margin: 2px 0 0;
  }

  .modal-close {
    margin-left: auto;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s;
  }

  .modal-close:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .modal-form {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    overflow-y: auto;
    flex: 1;
  }

  /* Dependency Visual */
  .dependency-visual {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 16px;
    background: var(--color-bg-primary);
    border-radius: 10px;
    border: 1px solid var(--color-border-subtle);
  }

  .dep-box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: var(--color-bg-secondary);
    border: 2px dashed var(--color-border-default);
    border-radius: 8px;
    min-width: 120px;
    justify-content: center;
    transition: all 0.2s;
  }

  .dep-box.selected {
    border-style: solid;
    border-color: var(--color-brand-primary);
    background: rgba(139, 0, 0, 0.05);
  }

  .dep-icon {
    font-size: 20px;
  }

  .dep-placeholder {
    font-size: 12px;
    color: var(--color-text-muted);
  }

  .dep-arrow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    color: var(--color-text-muted);
  }

  .dep-arrow-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-group label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }

  .form-group label .optional {
    font-weight: 400;
    text-transform: none;
  }

  .form-group select,
  .form-group input {
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid var(--color-border-default);
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-size: 14px;
    transition: all 0.15s;
  }

  .form-group select:focus,
  .form-group input:focus {
    outline: none;
    border-color: #8B0000;
    box-shadow: 0 0 0 3px rgba(139, 0, 0, 0.15);
  }

  .form-group select:disabled,
  .form-group input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Dependency Type Options */
  .dep-type-options {
    display: flex;
    gap: 10px;
  }

  .dep-type-btn {
    flex: 1;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    background: var(--color-bg-primary);
    border: 2px solid var(--color-border-default);
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
  }

  .dep-type-btn:hover {
    border-color: var(--color-border-hover);
  }

  .dep-type-btn.active {
    border-color: #8B0000;
    background: rgba(139, 0, 0, 0.05);
  }

  .dep-type-btn svg {
    color: var(--color-text-muted);
    margin-top: 2px;
  }

  .dep-type-btn.active svg {
    color: #8B0000;
  }

  .dep-type-title {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .dep-type-desc {
    display: block;
    font-size: 11px;
    color: var(--color-text-muted);
    margin-top: 2px;
  }

  .modal-actions {
    display: flex;
    gap: 10px;
    padding-top: 12px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .modal-actions button {
    flex: 1;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.15s;
    border: none;
  }

  .btn-cancel {
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default) !important;
  }

  .btn-cancel:hover:not(:disabled) {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .modal-actions .btn-primary {
    background: linear-gradient(135deg, #8B0000, #A31621);
    color: white;
    box-shadow: 0 4px 12px rgba(139, 0, 0, 0.3);
  }

  .modal-actions .btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(139, 0, 0, 0.4);
  }

  .modal-actions .btn-primary:disabled,
  .btn-cancel:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  @media (max-width: 480px) {
    .modal-container {
      width: 95%;
      border-radius: 12px;
    }

    .form-grid {
      grid-template-columns: 1fr;
    }

    .dep-type-options {
      flex-direction: column;
    }

    .dependency-visual {
      flex-direction: column;
    }

    .dep-arrow {
      transform: rotate(90deg);
    }
  }
`;
